import LoginForm from './LoginForm';
import { connect } from 'react-redux';
import { apiGetDispatchable } from '../../core/dapi';
import { noOp } from '../../actions';
import { setLoginInfo } from '../../core/session';
import {
  generateOtpSuccess,
  generateOtpFailure,
  accountFrozen,
  noAccount,
  loginSubmitButtonDisabled,
  resetLoginData,
  loginResponseReceived,
  loginSubmitted,
  loginFailed,
  loginFailMaxExceeded,
} from '../../actions/login';
import { postLoginDispatchable } from '../../core/dapi/login';
import { setSolvTosAccepted } from '../../core/dapi/account';
import {
  PHONE_LOGIN_SUBMIT,
  PHONE_LOGIN_SUBMIT_ERROR,
  PHONE_LOGIN_NUMBER_NOT_FOUND,
  PHONE_VERIFICATION_NOT_REQUIRED,
  PHONE_VERIFICATION_REQUIRED,
  PHONE_VERIFICATION_FAILED,
  ACCOUNT_FROZEN,
  PHONE_LOGIN_NUMBER_NOT_FOUND_CREATE_ACCOUNT,
  OTP_LOGIN_SUBMIT,
  OTP_LOGIN_WRONG_CODE,
  OTP_LOGIN_MAX_FAIL,
  OTP_LOGIN_SUCCESS,
} from '../../core/analytics/events';
import {
  analyticsTagHotjarRecording,
  analyticsTrackEvent,
  analyticsTrackFormErrors,
  analyticsTriggerHotjarRecording,
} from '../../core/analytics';
import { apiPostDispatchable } from '../../core/dapi/index';
import { getGenerateOneTimePasswordUrl } from '../../core/dapi/login';
import { getCreateAccountUrl, getVerificationStatus } from '../../core/dapi/account';
import { isTestPhoneNumber, sanitizePhone } from '../../core/util/phoneNumber';
import { hideModal, setRuntimeVariable } from '../../actions/runtime';
import { emptyFunction } from '../../core/util/function';
import { isEmptyObject } from '../../core/util/object';
import { withCookies } from 'react-cookie';
import { TEST_PHONE_NUMBER } from '../../constants/phoneLoginForm';
import { isAndroidApp, isIOS, isIosApp } from '../../core/util/device';

const ACCOUNT_NOT_FOUND = 'Account not found';
const ACCOUNT_VERIFICATION_FAILED = 'Could not verify last name';
const ACCOUNT_IS_FROZEN = 'Account is frozen';
const VERIFICATION_STATUS_SUBMITTING = 'vss';
const VERIFICATION_STATUS_VERIFIED = 'vsv';
const VERIFICATION_STATUS_NOT_VERIFIED = 'vsnv';
const VERIFICATION_STATUS_ERROR = 'vse';

const mapStateToProps = state => ({
  login: state.login,
  initialValues: {
    lastName: '',
    phone:
      (state.login && state.login.login && state.login.login.phone) ||
      (state.newBooking && state.newBooking.lastCreatedBookingPhone) ||
      '',
  },
  verificationStatus: state.runtime.phoneVerificationStatus,
  requiresTosConsent: !!state.runtime.requiresTosConsent,
  accountSummary: state.account.summary || {},
  isLoginModalOpen: !isEmptyObject(state.runtime.modal) ? state.runtime.modal.loginModal : false,
  location: state.location,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  hideLoginModal: () => dispatch(hideModal('loginModal')),

  handleFormSubmit: (form, onSubmitError = emptyFunction) => {
    const phone = sanitizePhone(form.phone);
    if (isTestPhoneNumber(phone)) {
      analyticsTriggerHotjarRecording(TEST_PHONE_NUMBER);
      analyticsTagHotjarRecording(TEST_PHONE_NUMBER);
    }

    const handleAccountCreateSuccess = () => {
      dispatch(
        apiPostDispatchable(
          getGenerateOneTimePasswordUrl(),
          { phone },
          generateOtpSuccess(phone),
          generateOtpFailure
        )
      );
      return noOp();
    };

    const handleAccountCreation = () => {
      dispatch(
        apiPostDispatchable(
          getCreateAccountUrl(),
          { phone },
          handleAccountCreateSuccess,
          noAccount(phone)
        )
      );
      return noOp();
    };

    const handleGenerateOtpFailure = response => {
      analyticsTrackFormErrors(PHONE_LOGIN_SUBMIT_ERROR, { ...response });

      if (response === ACCOUNT_IS_FROZEN) {
        onSubmitError(ACCOUNT_FROZEN);
        analyticsTrackEvent(ACCOUNT_FROZEN);
        return accountFrozen(phone);
      }

      if (response === ACCOUNT_VERIFICATION_FAILED) {
        onSubmitError(PHONE_VERIFICATION_FAILED);
        analyticsTrackEvent(PHONE_VERIFICATION_FAILED);
      }

      if (response === ACCOUNT_NOT_FOUND) {
        analyticsTrackEvent(PHONE_LOGIN_NUMBER_NOT_FOUND_CREATE_ACCOUNT);
        return handleAccountCreation();
      }

      onSubmitError(PHONE_LOGIN_NUMBER_NOT_FOUND);
      analyticsTrackEvent(PHONE_LOGIN_NUMBER_NOT_FOUND);
      dispatch(loginSubmitButtonDisabled(false));
      return generateOtpFailure(response);
    };

    analyticsTrackEvent(PHONE_LOGIN_SUBMIT);
    dispatch(
      apiPostDispatchable(
        getGenerateOneTimePasswordUrl(),
        { phone, last_name: form.lastName },
        generateOtpSuccess(phone),
        handleGenerateOtpFailure
      )
    );
  },

  verifyPhone: phone => {
    const onSuccess = response => {
      dispatch(
        setRuntimeVariable({
          name: 'requiresTosConsent',
          value: response.requires_tos_consent_initial,
        })
      );

      if (response.requires_verification) {
        analyticsTrackEvent(PHONE_VERIFICATION_REQUIRED);
        return setRuntimeVariable({
          name: 'phoneVerificationStatus',
          value: VERIFICATION_STATUS_NOT_VERIFIED,
        });
      }

      analyticsTrackEvent(PHONE_VERIFICATION_NOT_REQUIRED);
      return setRuntimeVariable({
        name: 'phoneVerificationStatus',
        value: VERIFICATION_STATUS_VERIFIED,
      });
    };

    const onError = () =>
      setRuntimeVariable({
        name: 'phoneVerificationStatus',
        value: VERIFICATION_STATUS_ERROR,
      });

    dispatch(
      setRuntimeVariable({
        name: 'phoneVerificationStatus',
        value: VERIFICATION_STATUS_SUBMITTING,
      })
    );
    return dispatch(apiGetDispatchable(getVerificationStatus(phone), onSuccess, onError));
  },

  setVerificationStatus: status =>
    dispatch(setRuntimeVariable({ name: 'phoneVerificationStatus', value: status })),

  resetLoginData: () => dispatch(resetLoginData()),

  handleOtpFormSubmit: (form, cookies, onSubmitError = emptyFunction) => {
    const onSuccessfulLogin = response => {
      setLoginInfo(response, cookies);
      analyticsTrackEvent(OTP_LOGIN_SUCCESS);

      if (form.tosAccepted) {
        dispatch(
          apiPostDispatchable(
            setSolvTosAccepted(response.account_id),
            {
              is_ios_user: isIOS(),
              is_ios_app_user: isIosApp(),
              is_android_app_user: isAndroidApp(),
            },
            () =>
              setRuntimeVariable({
                name: 'requiresTosConsent',
                value: false,
              }),
            noOp
          )
        );
      }

      return loginResponseReceived(response);
    };

    analyticsTrackEvent(OTP_LOGIN_SUBMIT);
    dispatch(loginSubmitted(true));

    const handleLoginFailed = response => {
      if (response === 'Maximum attempts exceeded') {
        onSubmitError(OTP_LOGIN_MAX_FAIL);
        analyticsTrackEvent(OTP_LOGIN_MAX_FAIL);
        return loginFailMaxExceeded(response);
      }

      onSubmitError(OTP_LOGIN_WRONG_CODE);
      analyticsTrackEvent(OTP_LOGIN_WRONG_CODE);
      return loginFailed(response);
    };

    dispatch(postLoginDispatchable(form, onSuccessfulLogin, handleLoginFailed));
  },
});

export default withCookies(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(LoginForm)
);
